/**
 * Refinance wizard (WI-TV-W5-001 foundation, completed by WI-TV-W5-004).
 *
 * The rendering, navigation, submission, and thank-you behavior live in the
 * shared wizard shell (`wizard-shell.ts`), which WI-TV-W6-001 extracted from
 * this module so the purchase journey renders from the same primitives. This
 * module keeps the refinance-specific wiring: the locked refinance step list,
 * the refinance prefill sanitizer, and the refinance copy. Its behavior is
 * unchanged, apart from the optional shared pre-submit "Change loan type"
 * affordance the journey owner may wire.
 */
import type { WizardMachine, WizardStep } from "./wizard-machine";
import { createRefinanceSteps } from "./refinance-steps";
import type { MaybePromise } from "../callbacks";
import type {
  RefinanceSubmissionDeps,
  RefinanceSubmissionOutcome,
  RefinanceSubmitter,
} from "./refinance-submission";
import {
  renderWizardShell,
  type WizardPrefillApplier,
  type WizardSnapshotReader,
  type WizardSwitchProbe,
} from "./wizard-shell";

export type RefinancePrefillApplier = WizardPrefillApplier;

export interface RefinanceWizardOptions {
  /** Step list override, primarily for tests. Defaults to the locked flow. */
  steps?: WizardStep[];
  /** Initial values (e.g. from prefill), primarily for tests. */
  initialValues?: Record<string, string>;
  /**
   * Submission wiring. When present the final step submits the referral to LMD;
   * when absent the wizard is navigation-only (used by foundation tests).
   */
  submission?: RefinanceSubmissionDeps;
  /** Submitter override, primarily for tests that assert call counts. */
  submitter?: RefinanceSubmitter;
  /**
   * Called once the wizard is closed out: after a successful submission when a
   * submitter is wired, or immediately on completing the final step when it is
   * navigation-only.
   */
  onComplete?: (values: Readonly<Record<string, string>>) => MaybePromise<void>;
  /** Observes each submission outcome, primarily for tests. */
  onSubmitOutcome?: (outcome: RefinanceSubmissionOutcome) => MaybePromise<void>;
  /**
   * Receives the wizard's prefill applier once the wizard is mounted, so the
   * caller can bridge inbound `TURBOVETS_PREFILL` messages into the wizard's
   * values on the entry paths where no legacy form is mounted to apply them to.
   */
  onPrefillBridge?: (apply: RefinancePrefillApplier) => void;
  /** Receives a reader for the wizard's current values once it is mounted. */
  onSnapshotBridge?: (readSnapshot: WizardSnapshotReader) => void;
  /**
   * Wires the shared pre-submit "Change loan type" affordance (WI-TV-W6-001).
   * Omitted on the entry paths that mount the wizard on its own, so refinance
   * behaves exactly as it did before Wave 6 there.
   */
  changeLoanType?: {
    onActivate: () => void;
    onProbe?: (canSwitch: WizardSwitchProbe) => void;
  };
}

/**
 * Renders the refinance wizard into `root` and returns the {@link WizardMachine}
 * driving it so callers/tests can inspect navigation state.
 */
export function renderRefinanceWizard(
  root: HTMLElement,
  options: RefinanceWizardOptions = {},
): WizardMachine {
  return renderWizardShell(root, {
    steps: options.steps ?? createRefinanceSteps(),
    ...(options.initialValues ? { initialValues: options.initialValues } : {}),
    headerLabel: "Refinance",
    referralNoun: "refinance referral",
    completion: {
      title: "Foundation complete",
      body:
        "You've reached the end of the refinance wizard foundation. Submission and the " +
        "thank-you experience are delivered in a later work item.",
    },
    ...(options.submission ? { submission: options.submission } : {}),
    ...(options.submitter ? { submitter: options.submitter } : {}),
    ...(options.onComplete ? { onComplete: options.onComplete } : {}),
    ...(options.onSubmitOutcome ? { onSubmitOutcome: options.onSubmitOutcome } : {}),
    ...(options.onPrefillBridge ? { onPrefillBridge: options.onPrefillBridge } : {}),
    ...(options.onSnapshotBridge ? { onSnapshotBridge: options.onSnapshotBridge } : {}),
    ...(options.changeLoanType ? { changeLoanType: options.changeLoanType } : {}),
  });
}

export { escapeWizardHtml } from "./wizard-shell";
